import { useState, useRef, type ReactNode, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "@/components/Select";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { SearchInput } from "@/components/SearchInput";
import { Badge } from "@/components/Badge";
import { Switch } from "@/components/Switch";
import { focusableElements } from "@/components/overlay";

export interface MultiSelectOption {
    id: number;
    name: string;
    subtitle?: string;
    badge?: ReactNode;
}

export interface MultiSelectGroup {
    id: number;
    name: string;
    subcategories: MultiSelectOption[];
}

export interface SearchableMultiSelectProps {
    label?: ReactNode;
    helperText?: ReactNode;
    groups?: MultiSelectGroup[];
    options?: MultiSelectOption[];
    value: number[];
    onChange: (value: number[]) => void;
    placeholder?: string;
    className?: string;
    singleSelect?: boolean;
    hideFullscreen?: boolean;
    isFullscreen?: boolean;
    onFullscreenChange?: (fullscreen: boolean) => void;
    clearable?: boolean;
    disabled?: boolean;
    size?: "sm" | "md";
    onAdd?: () => void;
    selectionControl?: "check" | "switch";
}

export const SearchableMultiSelect = ({
    label,
    helperText,
    groups,
    options,
    value,
    onChange,
    placeholder,
    className = "",
    singleSelect = false,
    hideFullscreen = false,
    isFullscreen: controlled,
    onFullscreenChange,
    clearable = true,
    disabled = false,
    size = "md",
    onAdd,
    selectionControl = "check",
}: SearchableMultiSelectProps) => {
    const { t } = useTranslation("global");
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState("");
    const list = useRef<HTMLDivElement>(null);
    const all = options?.length
        ? options
        : (groups || []).flatMap((group) =>
              group.subcategories.map((option) => ({
                  ...option,
                  subtitle: [group.name, option.subtitle]
                      .filter(Boolean)
                      .join(" · "),
              }))
          );
    const full = controlled ?? expanded;
    const title =
        typeof label === "string" ? label : placeholder || t("common.search");
    const expand = (next: boolean): void => {
        setExpanded(next);
        onFullscreenChange?.(next);
        setSearch("");
    };
    const choose = (id: number): void => {
        if (singleSelect) {
            onChange(value.includes(id) ? [] : [id]);
            expand(false);
        } else {
            onChange(
                value.includes(id)
                    ? value.filter((selected) => selected !== id)
                    : [...value, id]
            );
        }
    };
    const keyboard = (event: KeyboardEvent): void => {
        if (
            !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) ||
            !list.current
        ) {
            return;
        }
        event.preventDefault();
        const items = focusableElements(list.current);
        const index = items.indexOf(document.activeElement as HTMLElement);
        const next =
            event.key === "Home"
                ? 0
                : event.key === "End"
                  ? items.length - 1
                  : (index +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        items.length) %
                    items.length;
        items[next]?.focus();
    };
    const visible = all.filter((option) =>
        (option.name + " " + (option.subtitle || ""))
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase())
    );
    return (
        <div className={`space-y-1 ${className}`}>
            {label && (
                <div className="mb-1 flex items-end justify-between gap-2">
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300">
                        {label}
                    </label>
                    <div className="ml-auto flex items-end gap-1.5 leading-none">
                        {onAdd && (
                            <Button
                                variant="ghost"
                                onlyicon
                                size="xs"
                                icon="plus"
                                disabled={disabled}
                                aria-label={t("common.add")}
                                onClick={onAdd}
                            />
                        )}
                        {!hideFullscreen && (
                            <Button
                                variant="ghost"
                                onlyicon
                                size="xs"
                                icon="maximize"
                                className="!h-5 !w-5 !min-h-5 !min-w-5"
                                disabled={disabled}
                                aria-label={t("common.fullscreen")}
                                onClick={() => expand(true)}
                            />
                        )}
                    </div>
                </div>
            )}
            <div className="flex items-end gap-2">
                <Select<number>
                    label={label ? undefined : title}
                    value={singleSelect ? (value[0] ?? "") : value}
                    multiple={!singleSelect}
                    disabled={disabled}
                    clearable={clearable}
                    searchable
                    full
                    size={size}
                    options={all.map((option) => ({
                        value: option.id,
                        label: option.name,
                        subtitle: option.subtitle,
                        icon: option.badge,
                    }))}
                    onChange={(selected: number | number[]) =>
                        onChange(
                            Array.isArray(selected)
                                ? selected
                                : selected
                                  ? [selected]
                                  : []
                        )
                    }
                    placeholder={placeholder || t("common.search")}
                />
                {!label && onAdd && (
                    <Button
                        variant="ghost"
                        onlyicon
                        icon="plus"
                        disabled={disabled}
                        aria-label={t("common.add")}
                        onClick={onAdd}
                    />
                )}
                {!label && !hideFullscreen && (
                    <Button
                        variant="ghost"
                        onlyicon
                        size="sm"
                        icon="maximize"
                        className="!h-7 !w-7 !min-h-7 !min-w-7"
                        disabled={disabled}
                        aria-label={t("common.fullscreen")}
                        onClick={() => expand(true)}
                    />
                )}
            </div>
            {helperText && (
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                    {helperText}
                </p>
            )}
            {!singleSelect && value.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {all
                        .filter((option) => value.includes(option.id))
                        .map((option) => (
                            <Badge key={option.id} variant="neutral">
                                {option.name}
                            </Badge>
                        ))}
                </div>
            )}
            <Modal
                isOpen={full}
                isFullscreen
                onClose={() => expand(false)}
                title={title}
                hideFullscreen
                protectChanges={false}
                cancelLabel={t("common.close")}
            >
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    aria-label={title}
                    onKeyDown={keyboard}
                />
                {!singleSelect && value.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 px-1 py-2">
                        {all
                            .filter((option) => value.includes(option.id))
                            .map((option) => (
                                <Badge key={option.id} variant="primary">
                                    {option.name}
                                </Badge>
                            ))}
                    </div>
                )}
                <div
                    ref={list}
                    role="listbox"
                    aria-label={title}
                    aria-multiselectable={!singleSelect}
                    onKeyDown={keyboard}
                    className="space-y-1"
                >
                    {visible.map((option) => selectionControl === "switch" ? (
                        <div
                            key={option.id}
                            role="option"
                            aria-selected={value.includes(option.id)}
                            onClick={() => choose(option.id)}
                            className="w-full cursor-pointer rounded-md px-3 py-1 hover:bg-slate-100 dark:hover:bg-white/5"
                        >
                            <div
                                className="flex w-full items-center justify-between gap-3"
                            >
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <div className="truncate text-xs font-semibold text-slate-700 dark:text-zinc-200">
                                        {option.name}
                                    </div>
                                    {option.subtitle && (
                                        <div className="truncate text-xs text-slate-500 dark:text-zinc-400">
                                            {option.subtitle}
                                        </div>
                                    )}
                                </div>
                                <Switch
                                    checked={value.includes(option.id)}
                                    onChange={() => choose(option.id)}
                                />
                            </div>
                        </div>
                    ) : (
                        <Button
                            key={option.id}
                            role="option"
                            aria-selected={value.includes(option.id)}
                            variant="ghost"
                            icon={value.includes(option.id) ? "check" : "square"}
                            onClick={() => choose(option.id)}
                            className="!w-full !justify-start !px-3"
                            contentClassName="!justify-start"
                        >
                            <span className="flex flex-col items-start gap-1">
                                <span>{option.name}</span>
                                {option.subtitle && (
                                    <span className="text-xs text-slate-500">
                                        {option.subtitle}
                                    </span>
                                )}
                            </span>
                            {option.badge}
                        </Button>
                    ))}
                    {!visible.length && (
                        <p role="status" className="p-4 text-sm text-slate-500">
                            {t("common.no_results")}
                        </p>
                    )}
                </div>
            </Modal>
        </div>
    );
};
